import {
    ALGOLIA_INDEX,
    ALGOLIA_SPOTLIGHTS_INDEX,
    DEFAULT_SEARCHABLE_ATTRIBUTES,
} from '@/config/constants';
import {
    Command,
    CommandGroup,
    CommandInput,
    CommandList,
} from '@/ui/design-system/components/ui/command';
import {
    Configure,
    Index,
    InstantSearch,
    connectSearchBox,
    connectStateResults,
} from 'react-instantsearch-dom';
import { MessageCircle, SearchX, X } from 'lucide-react';
import {
    createProxySearchClient,
    sendAlgoliaInsightEvent,
} from 'utils/api/algoliaSearchProxy';
import { getPathFromNode, isEmptyStr, isSpateUser } from 'utils/helpers';
import {
    useConditionallyDebouncedInput,
    useCountryParam,
    usePlan,
} from 'utils/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Button } from '@/ui/design-system/components/ui/button';
import { DATA_SOURCES } from 'utils/metrics';
import SearchResult from './SearchResult';
import SearchResultsSkeleton from './SearchResultsSkeleton';
import SpotlightResults from './SpotlightResults';
import SuggestDialog from './SuggestDialog';
import { TooltipProvider } from '@/ui/design-system/components/ui/tooltip';
import bugsnagClient from 'containers/BugsnagClient';
import { createPath } from 'history';
import { filterAndFormatDuplicateHits } from 'utils/search';
import { mixpanelTrack } from 'utils/app';
import { trackNavigation } from 'components/Link';
import { useAssistantContext } from 'components/AiAssistant';
import useGetHierarchy from 'utils/hooks/useGetHierarchy';
import { useSelector } from 'react-redux';

const GROUP_HEADINGS = {
    category: 'Categories',
    brand: 'Brands',
    affinity: 'Related Searches',
    product: 'Products',
};

const GROUP_CONFIG = [
    {
        width: 'w-full md:w-1/2 lg:w-1/3',
        grid: '[&_[cmdk-group-items]]:grid-cols-1',
    },
    {
        width: 'w-full lg:w-2/3',
        grid: '[&_[cmdk-group-items]]:grid-cols-1 [&_[cmdk-group-items]]:md:grid-cols-2',
    },
    {
        width: 'w-full',
        grid: '[&_[cmdk-group-items]]:grid-cols-1 [&_[cmdk-group-items]]:md:grid-cols-2 [&_[cmdk-group-items]]:lg:grid-cols-3',
    },
];

function getGroupClasses(itemCount) {
    const cols = Math.min(Math.ceil(itemCount / 10), 3);
    const { width, grid } = GROUP_CONFIG[cols - 1];
    return `float-left mb-3 ${width} ${grid} [&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:md:grid-flow-col [&_[cmdk-group-items]]:md:grid-rows-[repeat(10,auto)] [&_[cmdk-group-items]]:md:gap-x-2`;
}

function SearchCommandContent({
    searchState,
    allSearchResults,
    searchResults,
    searching,
    error,
    refine,
    open,
    onClose,
}) {
    const location = useLocation();
    const history = useHistory();
    const getHierarchy = useGetHierarchy();
    const userToken = useSelector(({ session }) => session?.mixpanelUserId);
    const { mutateToWithCountryParam } = useCountryParam();
    const { openPanel, sendMessage } = useAssistantContext();

    const queryID = searchResults?.queryID ?? null;
    const query = searchState?.query ?? null;
    const hits = searchResults?.hits ?? null;

    // Restore query from location state after navigating to a result
    const defaultQuery = useMemo(
        () => location.state?.fromQuery || '',
        [location],
    );

    const inputRef = useRef(null);
    const [didFetch, setDidFetch] = useState(false);
    const [suggestOpen, setSuggestOpen] = useState(false);

    useEffect(() => {
        if (searching === false) setDidFetch(false);
    }, [searching]);

    // Focus input when modal opens
    useEffect(() => {
        if (open) {
            // Slight delay to ensure the element is visible before focusing
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [open]);

    // Close modal on navigation (skip initial mount)
    const locationRef = useRef(location);
    useEffect(() => {
        if (locationRef.current !== location) {
            locationRef.current = location;
            if (onClose) onClose();
        }
    }, [location]);

    const MAX_QUERY_LENGTH = 512;

    const notEmptyOrResultQuery = (value) =>
        !(isEmptyStr(value) || value === query);

    const handleDebouncedInputChange = (value) => {
        if (value.length > MAX_QUERY_LENGTH) return;
        if (notEmptyOrResultQuery(value)) {
            mixpanelTrack('SearchInput', { query: value });
            setDidFetch(true);
            refine(value);
        }
    };

    const { inputValue, isTyping, onInputChange } =
        useConditionallyDebouncedInput({
            value: defaultQuery,
            debounceTimeout: 800,
            onChange: handleDebouncedInputChange,
            shouldDebounce: notEmptyOrResultQuery,
        });

    const queryTooLong = inputValue.length > MAX_QUERY_LENGTH;

    // Adapter: cmdk fires onValueChange(string), hook expects event
    const handleValueChange = (val) => {
        onInputChange({ target: { value: val } });
    };

    // Spotlight hits from secondary index
    let spotlightHits;
    if (allSearchResults?.[ALGOLIA_SPOTLIGHTS_INDEX]?.hits) {
        spotlightHits = allSearchResults[ALGOLIA_SPOTLIGHTS_INDEX].hits;
    }

    const hasQuery = !isEmptyStr(inputValue);
    const isLoading = didFetch || isTyping;
    const hasResults = hits && hits.length > 0;

    const getRouteForNode = (node) => {
        const dataSource = !node.hasSearchVolume
            ? DATA_SOURCES.TIKTOK.key
            : null;
        return getPathFromNode(
            node.type === 'product'
                ? { ...node, type: 'brand', id: node.brandId }
                : node,
            null,
            dataSource,
        );
    };

    const trackResultSelect = (node, position, route) => {
        const { objectID, type, name } = node;

        if (queryID && objectID) {
            sendAlgoliaInsightEvent([
                {
                    eventType: 'click',
                    eventName: 'Search Result Clicked',
                    index: ALGOLIA_INDEX,
                    userToken,
                    queryID,
                    objectIDs: [objectID],
                    positions: [position],
                },
                {
                    eventType: 'conversion',
                    eventName: 'Search Result Conversion',
                    index: ALGOLIA_INDEX,
                    userToken,
                    queryID,
                    objectIDs: [objectID],
                },
            ]);
        } else {
            bugsnagClient.notify(
                `Missing queryID or objectID for Algolia events: queryID=${queryID}, objectID=${objectID}`,
            );
        }

        mixpanelTrack('SearchResult', {
            searchQuery: query,
            resultName: name,
            resultType: type,
        });

        trackNavigation(location.pathname, route, 'SearchResult', {
            searchQuery: query,
        });
    };

    const handleResultSelect = (node, position) => {
        const route = getRouteForNode(node);
        trackResultSelect(node, position, route);
        history.push(mutateToWithCountryParam(route), { fromQuery: query });
        if (onClose) onClose();
    };

    const handleResultModifierClick = (node, position) => {
        const route = getRouteForNode(node);
        trackResultSelect(node, position, route);
        const loc = mutateToWithCountryParam(route);
        window.open(typeof loc === 'string' ? loc : createPath(loc), '_blank');
    };

    const handleClear = () => {
        handleValueChange('');
        refine('');
        inputRef.current?.focus();
    };

    const handleAskSpateAi = () => {
        mixpanelTrack('Search AI Trigger Clicked', { query: inputValue });
        if (onClose) onClose();
        openPanel();
        sendMessage(inputValue, {
            newChat: true,
            source: 'search',
            skipPageContext: true,
        });
    };

    return (
        <>
            <div className="flex items-center [&_[cmdk-input-wrapper]]:border-b-0 [&_[cmdk-input-wrapper]]:flex-1 border-b border-border">
                <CommandInput
                    ref={inputRef}
                    placeholder="Search categories, brands, ingredients, products, or hashtags"
                    value={inputValue}
                    onValueChange={handleValueChange}
                />
                {hasQuery && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="mr-3 shrink-0 rounded-sm text-muted-foreground hover:text-foreground"
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Clear search</span>
                    </button>
                )}
            </div>
            {hasQuery && (
                <button
                    type="button"
                    onClick={handleAskSpateAi}
                    className="flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                >
                    <MessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium">Ask Spate AI</span>
                    <span className="text-muted-foreground">&mdash;</span>
                    <span className="truncate text-muted-foreground">
                        &ldquo;{inputValue}&rdquo;
                    </span>
                </button>
            )}
            {hasQuery && (
                <CommandList className="max-h-[60vh]">
                    {queryTooLong ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                            Search query is too long (max {MAX_QUERY_LENGTH}{' '}
                            characters).
                        </div>
                    ) : isLoading ? (
                        <SearchResultsSkeleton />
                    ) : error ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                            {error.message ||
                                'Search is temporarily unavailable. Please try again.'}
                        </div>
                    ) : (
                        <>
                            {hasResults ? (
                                filterAndFormatDuplicateHits(
                                    hits,
                                    getHierarchy,
                                ).map(([type, typeHits]) => (
                                    <CommandGroup
                                        key={type}
                                        heading={GROUP_HEADINGS[type] || type}
                                        className={getGroupClasses(
                                            typeHits.length,
                                        )}
                                    >
                                        {typeHits.map(
                                            ({ objectID, ...rest }, idx) => (
                                                <SearchResult
                                                    key={objectID}
                                                    hit={{ ...rest, objectID }}
                                                    onSelect={() =>
                                                        handleResultSelect(
                                                            {
                                                                ...rest,
                                                                objectID,
                                                            },
                                                            idx + 1,
                                                        )
                                                    }
                                                    onModifierClick={() =>
                                                        handleResultModifierClick(
                                                            {
                                                                ...rest,
                                                                objectID,
                                                            },
                                                            idx + 1,
                                                        )
                                                    }
                                                />
                                            ),
                                        )}
                                    </CommandGroup>
                                ))
                            ) : (
                                <div className="flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground">
                                    <SearchX className="h-4 w-4 shrink-0" />
                                    No results for &ldquo;{inputValue}&rdquo;
                                </div>
                            )}

                            {spotlightHits && spotlightHits.length > 0 && (
                                <SpotlightResults
                                    hits={spotlightHits}
                                    onClose={onClose}
                                />
                            )}
                        </>
                    )}
                </CommandList>
            )}
            {hasQuery && !isLoading && !error && (
                <div className="flex items-center justify-center gap-4 border-t border-border bg-muted px-2 py-3 text-sm text-foreground">
                    Don&apos;t see what you are looking for?
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSuggestOpen(true)}
                    >
                        Suggest
                    </Button>
                </div>
            )}
            <SuggestDialog
                open={suggestOpen}
                onOpenChange={setSuggestOpen}
                defaultSuggestion={inputValue}
            />
        </>
    );
}

const ConnectedSearchCommandContent = [
    connectStateResults,
    connectSearchBox,
].reduce((component, fn) => fn(component), SearchCommandContent);

function SearchModal({ open, onOpenChange }) {
    const currentCountry = useSelector(({ geo }) => geo);
    const { currentCountryParam } = useCountryParam();
    const analyticsTags = useSelector(({ session }) => [
        session &&
        isSpateUser({
            company: session.company,
            email: session.user_email,
        })
            ? 'spate'
            : 'notSpate',
    ]);

    const searchClient = useMemo(() => createProxySearchClient(), []);
    const { industry: userIndustries } = usePlan();
    const industry =
        userIndustries && userIndustries.length
            ? userIndustries.filter(
                  (name) => !['manipulation', 'dogs', 'flavors'].includes(name),
              )
            : null;

    const facetFilters = [];

    // One OR group: the record has data in this market, or a source we do not
    // gate search on carries it anywhere. Each such source needs its own term --
    // the index merges a source's markets into `country`, so without one an
    // entity only that source carries is unfindable in every market but its own.
    if (currentCountry || currentCountryParam) {
        facetFilters.push([
            `country:${currentCountry ? currentCountry.text_id : currentCountryParam}`,
            'hasTikTokViews:1',
            'hasInstagramLikes:1',
        ]);
    }

    let industryFacets = null;
    if (industry) {
        industryFacets = industry;
    }

    facetFilters.push(['type:-affinity']);

    if (industryFacets) {
        const relatedSearchCat1s = ['product specifics', 'body parts'];
        facetFilters.push([
            'type:-category',
            ...industryFacets
                .concat(relatedSearchCat1s)
                .map((cat1) => `industry:${cat1}`),
        ]);
        relatedSearchCat1s.forEach((relatedSearchCat1) => {
            facetFilters.push([
                'type:-category',
                `industry:-${relatedSearchCat1}`,
                'depth:cat5',
            ]);
        });
        ['brand', 'product'].forEach((type) => {
            facetFilters.push([
                `type:-${type}`,
                ...industryFacets
                    .concat(relatedSearchCat1s)
                    .map((cat1) => `industry:${cat1}`),
            ]);
        });
    }

    return (
        <InstantSearch indexName={ALGOLIA_INDEX} searchClient={searchClient}>
            <Index indexName={ALGOLIA_INDEX}>
                <Configure
                    analyticsTags={analyticsTags}
                    facetFilters={facetFilters}
                    restrictSearchableAttributes={DEFAULT_SEARCHABLE_ATTRIBUTES}
                    clickAnalytics
                />
            </Index>
            <Index indexName={ALGOLIA_SPOTLIGHTS_INDEX} />
            <div
                className={open ? 'fixed inset-0 z-50' : 'hidden'}
                role="dialog"
                aria-modal="true"
                aria-label="Search"
            >
                <div
                    className="absolute inset-0 bg-black/50"
                    onClick={() => onOpenChange(false)}
                />
                <div className="relative mx-auto mt-[10vh] max-w-4xl px-4">
                    <TooltipProvider delayDuration={300}>
                        <Command
                            shouldFilter={false}
                            className="rounded-lg border bg-popover shadow-lg [&_[cmdk-input-wrapper]_svg]:h-4 [&_[cmdk-input-wrapper]_svg]:w-4 [&_[cmdk-input]]:text-base [&_[cmdk-input]]:font-semibold [&_[cmdk-input]]:placeholder:font-normal"
                        >
                            <ConnectedSearchCommandContent
                                open={open}
                                onClose={() => onOpenChange(false)}
                            />
                        </Command>
                    </TooltipProvider>
                </div>
            </div>
        </InstantSearch>
    );
}

export default SearchModal;
